import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { ModalController, PopoverController } from '@ionic/angular';
import { TRenderOptionType, TSelectFilterGroupInfo } from '../filter/model/ui-filter.types';
import { MOBILE_SCREEN_UTILS } from '../utils/mobile-screen.utils';
import { TFilterSelected, TREQUEST_FABRIC_INSPECTION_FILTERS_SELECTED_FILTERS } from '@monorepo/types';
import {
  OrderUploadDataService,
  AqlListDataProvider,
  Plani8DataService,
  ProcessMappingDataProvider,
  FabricInspectionDataService,
  AppNotificationUtilClass,
} from '@monorepo/services';
import { uniq, cloneDeep } from 'lodash';
import { TranslateService } from '@ngx-translate/core';
import { lastValueFrom } from 'rxjs';

/**
 * @deprecated
 * Note: Below will be deprected and deleted since 'monorepo-inline-multi-select-depr' supports localization and is more generic
 */
@Component({
  selector: 'monorepo-multi-select-popover-depr',
  templateUrl: './multi-select-popover-depr.component.html',
  styleUrls: ['./multi-select-popover-depr.component.scss'],
})
export class MultiSelectPopoverDeprComponent implements OnInit, OnChanges {
  showClose = false;
  searchAPIResults: TSelectFilterGroupInfo['items'] = [];
  filteredPopoverInfo: TSelectFilterGroupInfo['items'] = [];
  _popoverInfo: TSelectFilterGroupInfo['items'] = [];
  searchPlaceholder = 'multi_select.Search';
  searchPlaceholderForLocalSerach = 'multi_select.Search';

  @Input() popoverHeader?: string;
  @Input() popoverBtnText?: string;
  @Input() set popoverInfo(value: TSelectFilterGroupInfo['items']) {
    this._popoverInfo = cloneDeep(value);
  }
  @Input() popoverRenderOptionType: TRenderOptionType = 'MODAL';
  @Input() filterKey!: string;
  @Input() displayName!: string;
  @Input() selectedFilters!: TFilterSelected[];
  @Input() searchWithApi: TSelectFilterGroupInfo['searchWithApi'] = undefined;
  @Input() virtualScroll?: TSelectFilterGroupInfo['virtualScroll'] = undefined;
  @Input() limitItemsByKeystroke?: TSelectFilterGroupInfo['limitItemsByKeystroke'] = undefined;

  @Output() applySelect = new EventEmitter<TSelectFilterGroupInfo['items']>();
  @Output() onModelChange = new EventEmitter<TSelectFilterGroupInfo['items']>();

  constructor(
    public popoverController: PopoverController,
    public modalController: ModalController,
    private plani8DataService: Plani8DataService,
    private processMappingDataService: ProcessMappingDataProvider,
    private orderUploadDataService: OrderUploadDataService,
    private transalteService: TranslateService,
    private aqlListDataProvider: AqlListDataProvider,
    private fabricInspectionDataService: FabricInspectionDataService,
    private appNotificationUtilClass: AppNotificationUtilClass,
  ) {
    this.showClose = MOBILE_SCREEN_UTILS.isMobileScreen();
  }

  ngOnInit() {
    if (this.displayName) {
      this.searchPlaceholder = `Search for ${this.displayName}`;
    }
    if (!this.searchWithApi && this.limitItemsByKeystroke) {
      this.searchPlaceholderForLocalSerach =
        this.transalteService.instant('multi_select.please_type_at_least') +
        ' ' +
        this.limitItemsByKeystroke.toString() +
        ' ' +
        this.transalteService.instant('multi_select.letters');
    } else {
      this.searchPlaceholderForLocalSerach = this.transalteService.instant(this.searchPlaceholderForLocalSerach);
    }
    //TODO[jyotishka] remove later
    this.filteredPopoverInfo =
      typeof this.limitItemsByKeystroke !== 'undefined' && this.limitItemsByKeystroke > 0 ? [] : this._popoverInfo;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['popoverInfo'] || changes['limitItemsByKeystroke']) {
      this.filteredPopoverInfo =
        typeof this.limitItemsByKeystroke !== 'undefined' && this.limitItemsByKeystroke > 0 ? [] : this._popoverInfo;
    }
  }

  onSearch($event: Event) {
    const searchValue = ($event.target as HTMLInputElement).value.toLowerCase();
    this.filteredPopoverInfo = this.isSearchValid(searchValue)
      ? this._popoverInfo.filter((item) => item.value.toString().toLocaleLowerCase().indexOf(searchValue) >= 0)
      : [];
  }

  isSearchValid(searchQuery: string): boolean {
    const minLength = this.limitItemsByKeystroke || 0;
    return searchQuery.length >= minLength;
  }

  onApply(data?: TSelectFilterGroupInfo['items']) {
    switch (this.popoverRenderOptionType) {
      case 'INLINE':
        this.popoverController.dismiss(data);
        break;
      case 'MODAL':
        this.modalController.dismiss(data);
        break;
      case 'TABS':
        this.applySelect.emit(data);
        break;
    }
  }

  onChange() {
    this.onModelChange.emit(this._popoverInfo);
  }

  onClose() {
    if (this.popoverRenderOptionType === 'INLINE') this.popoverController.dismiss();
    else this.modalController.dismiss();
  }

  async triggerApiSearch(searchText: string) {
    if (searchText === '') {
      this.searchAPIResults = [];
      return;
    }
    switch (this.searchWithApi?.searchService) {
      case 'plani8':
        {
          const extraPayload = this.searchWithApi.payload ? (this.searchWithApi.payload as typeof Object) : {};
          const loading = await this.appNotificationUtilClass.presentLoading();
          lastValueFrom(
            this.plani8DataService.getFiltersSearchResults(this.searchWithApi.searchEndPoint, {
              ...extraPayload,
              key: this.filterKey,
              searchString: searchText,
              selectedFilters: this.selectedFilters,
            }),
          )
            .finally(() => {
              this.appNotificationUtilClass.dismissLoadingV2(loading);
            })
            .then((res) => {
              this.searchAPIResults = res['searchResultItems'].map((r) => {
                const existingItem = this.filteredPopoverInfo.find((item) => item.value.toString() === r);
                if (existingItem) return existingItem;
                else {
                  return {
                    value: r,
                    isSelected: false,
                  };
                }
              });
            });
        }
        break;
      case 'process-mapping':
        {
          const extraPayload = this.searchWithApi.payload ? (this.searchWithApi.payload as typeof Object) : {};
          const loading = await this.appNotificationUtilClass.presentLoading();
          const selectedFiltersWithSearchable = this.selectedFilters.map((filter) => ({
            ...filter,
            ...(extraPayload && { searchable: true }),
          }));
          lastValueFrom(
            this.processMappingDataService.getFiltersSearchResults(this.searchWithApi.searchEndPoint, {
              ...extraPayload,
              key: this.filterKey,
              searchString: searchText,
              selectedFilters: selectedFiltersWithSearchable,
            }),
          )
            .finally(() => {
              loading.dismiss();
            })
            .then((res) => {
              this.searchAPIResults = res['response'].values.map((r) => {
                const existingItem = this.filteredPopoverInfo.find((item) => item.value.toString() === r);
                if (existingItem) return existingItem;
                else {
                  return {
                    value: r,
                    isSelected: false,
                  };
                }
              });
            });
        }
        break;
      case 'order-upload-search':
        {
          const loading = await this.appNotificationUtilClass.presentLoading();
          lastValueFrom(
            this.orderUploadDataService.getFiltersSearchResults(this.searchWithApi.searchEndPoint, {
              key: this.filterKey,
              searchString: searchText,
              selectedFilters: this.selectedFilters,
              subjectKey: '',
            }),
          )
            .finally(() => {
              this.appNotificationUtilClass.dismissLoadingV2(loading);
            })
            .then((res) => {
              this.searchAPIResults = res.response['values'].map((r) => {
                const existingItem = this.filteredPopoverInfo.find((item) => item.value.toString() === r);
                if (existingItem) return existingItem;
                else {
                  return {
                    value: r,
                    isSelected: false,
                  };
                }
              });
            });
        }
        break;
      case 'aql':
        {
          const loading = await this.appNotificationUtilClass.presentLoading();
          lastValueFrom(
            this.aqlListDataProvider.getFiltersSearchResults(this.searchWithApi.searchEndPoint, {
              key: this.filterKey,
              searchString: searchText,
              selectedFilters: this.selectedFilters,
              searchInOption: this.searchWithApi.searchInOption,
              department: this.searchWithApi.department,
            }),
          )
            .finally(() => {
              this.appNotificationUtilClass.dismissLoadingV2(loading);
            })
            .then((res) => {
              this.searchAPIResults = res['searchResponse']['values'].map((r) => {
                const existingItem = this.filteredPopoverInfo.find((item) => item.value.toString() === r);
                if (existingItem) return existingItem;
                else {
                  return {
                    value: r,
                    isSelected: false,
                  };
                }
              });
            });
        }
        break;
      case 'fabric-inspection':
        {
          const loading = await this.appNotificationUtilClass.presentLoading('Loading Results...');
          let finalSelectedFilters = cloneDeep(this.selectedFilters);
          finalSelectedFilters.forEach((obj) => {
            delete (obj as TREQUEST_FABRIC_INSPECTION_FILTERS_SELECTED_FILTERS)['displayName'];
          });
          lastValueFrom(
            this.fabricInspectionDataService.getFiltersSearchResults(this.searchWithApi.searchEndPoint, {
              appDetails: this.searchWithApi.appDetails,
              filterType: this.filterKey,
              searchString: searchText,
              filters: finalSelectedFilters,
            }),
          )
            .finally(() => {
              this.appNotificationUtilClass.dismissLoadingV2(loading);
            })
            .then((res) => {
              this.searchAPIResults = res['values'].map((r: string) => {
                const existingItem = this.filteredPopoverInfo.find((item) => item.value.toString() === r);
                if (existingItem) return existingItem;
                else {
                  return {
                    value: r,
                    isSelected: false,
                  };
                }
              });
            });
        }
        break;
    }
  }

  applySearchResultSelect($data: TSelectFilterGroupInfo['items']) {
    this.filteredPopoverInfo = uniq([...this.filteredPopoverInfo, ...$data]);

    this._popoverInfo = this.filteredPopoverInfo;
    console.log('onapply', this._popoverInfo, this.filteredPopoverInfo);

    this.onModelChange.emit(this._popoverInfo);
  }

  trackByFn(index: number, item: TSelectFilterGroupInfo['items'][number]): string | number {
    return item.id ?? item.value;
  }
}
